// pages/ProjectDetail.tsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Briefcase,
  Shield,
  CheckCircle,
  XCircle,
  Loader2,
  Play,
  FileCheck,
  ExternalLink,
  AlertCircle,
} from "lucide-react";
import {
  useGetUserProjectDetails,
  useApplyForProject,
  useStartProject,
  UserProjectStatus,
  ApplyForProjectPayload,
} from "../lib/api/projects";
import ProjectDetailSkeleton from "../components/projects/ProjectDetailSkeleton";
import ProjectApplicationModal from "../components/projects/ProjectApplicationModal";
import { useAuth } from "../context/AuthContext";

const ProjectDetail: React.FC = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  // const [copiedLink, setCopiedLink] = useState(false);

  // Fetch user project details
  const {
    data: userProject,
    isLoading,
    isError,
  } = useGetUserProjectDetails(user?.id, id);
  const applyMutation = useApplyForProject();
  const startMutation = useStartProject();

  // Access the nested project object
  const project = userProject?.project;

  const getCategoryConfig = (category: string) => {
    const configs: Record<string, { bg: string; text: string }> = {
      "AI Training": { bg: "bg-blue-100", text: "text-blue-700" },
      "Data Annotation": { bg: "bg-emerald-100", text: "text-emerald-700" },
      "Code Review": { bg: "bg-purple-100", text: "text-purple-700" },
      "Content Moderation": { bg: "bg-amber-100", text: "text-amber-700" },
      "Audio Transcription": { bg: "bg-rose-100", text: "text-rose-700" },
      "Sentiment Analysis": { bg: "bg-cyan-100", text: "text-cyan-700" },
      "Prompt Engineering": { bg: "bg-indigo-100", text: "text-indigo-700" },
      "Image Classification": { bg: "bg-teal-100", text: "text-teal-700" },
      "Text Review": { bg: "bg-orange-100", text: "text-orange-700" },
      "Video Annotation": { bg: "bg-pink-100", text: "text-pink-700" },
    };
    return configs[category] || { bg: "bg-gray-100", text: "text-gray-700" };
  };

  const getStatusConfig = (status: UserProjectStatus) => {
    const configs: Record<
      UserProjectStatus,
      {
        bg: string;
        border: string;
        text: string;
        icon: React.ElementType;
        label: string;
        animate?: boolean;
      }
    > = {
      assigned: {
        bg: "bg-blue-50",
        border: "border-blue-200",
        text: "text-blue-700",
        icon: AlertCircle,
        label: "Available to Apply",
      },
      pending: {
        bg: "bg-amber-50",
        border: "border-amber-200",
        text: "text-amber-700",
        icon: Loader2,
        label: "Verification Pending",
        animate: true,
      },
      approved: {
        bg: "bg-emerald-50",
        border: "border-emerald-200",
        text: "text-emerald-700",
        icon: CheckCircle,
        label: "Approved - Ready to Start",
      },
      active: {
        bg: "bg-blue-50",
        border: "border-blue-200",
        text: "text-blue-700",
        icon: Play,
        label: "Active",
      },
      completed: {
        bg: "bg-emerald-50",
        border: "border-emerald-200",
        text: "text-emerald-700",
        icon: FileCheck,
        label: "Completed",
      },
      rejected: {
        bg: "bg-red-50",
        border: "border-red-200",
        text: "text-red-700",
        icon: XCircle,
        label: "Rejected",
      },
      terminated: {
        bg: "bg-gray-100",
        border: "border-gray-200",
        text: "text-gray-700",
        icon: XCircle,
        label: "Terminated",
      },
    };
    return configs[status] || configs.assigned;
  };

  const handleApply = async (
    payload: ApplyForProjectPayload
  ): Promise<{ verificationLink: string }> => {
    if (!user?.id || !id) return { verificationLink: "" };

    const result = await applyMutation.mutateAsync({
      userId: user.id,
      userProjectId: id,
      payload,
    });

    const verificationLink =
      result.data?.verificationLink || userProject?.verificationLink || "";
    return { verificationLink };
  };

  const handleStartProject = async () => {
    if (!user?.id || !id) return;

    await startMutation.mutateAsync({
      userId: user.id,
      userProjectId: id,
    });
  };

  const handleGoToVerification = () => {
    if (userProject?.verificationLink) {
      window.open(userProject.verificationLink, "_blank");
    }
  };

  // const handleCopyLink = async () => {
  //   if (userProject?.verificationLink) {
  //     try {
  //       await navigator.clipboard.writeText(userProject.verificationLink);
  //       setCopiedLink(true);
  //       setTimeout(() => setCopiedLink(false), 2000);
  //     } catch (err) {
  //       console.error("Failed to copy link:", err);
  //     }
  //   }
  // };

  if (isLoading) {
    return <ProjectDetailSkeleton />;
  }

  if (isError || !userProject || !project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Project Not Found
          </h2>
          <p className="text-gray-500 mb-4">
            The project you're looking for doesn't exist or you don't have
            access to it.
          </p>
          <button
            onClick={() => navigate("/projects")}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#8B52FF] text-white rounded-lg"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  const categoryConfig = getCategoryConfig(project.category);
  const statusConfig = getStatusConfig(userProject.status);
  const StatusIcon = statusConfig.icon;

  const getActionButtonConfig = () => {
    switch (userProject.status) {
      case "assigned":
        return {
          text: "Apply for This Project",
          onClick: () => setShowApplicationModal(true),
          disabled: false,
          loading: false,
        };
      case "approved":
        return {
          text: startMutation.isPending ? "Starting..." : "Start Working",
          onClick: handleStartProject,
          disabled: startMutation.isPending,
          loading: startMutation.isPending,
          icon: startMutation.isPending ? Loader2 : Play,
        };
      case "active":
        return {
          text: "Go to Project Platform",
          onClick: handleGoToVerification,
          disabled: false,
          loading: false,
          icon: ExternalLink,
        };
      default:
        return null;
    }
  };

  const actionButton = getActionButtonConfig();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Back Button */}
      <button
        onClick={() => navigate("/projects")}
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Projects
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Header Card */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
              <div>
                <span
                  className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${categoryConfig.bg} ${categoryConfig.text} mb-3`}
                >
                  {project.category.toUpperCase()}
                </span>
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
                  {project.title}
                </h1>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-gray-900">
                  ${project.hourlyRate}
                </p>
                <p className="text-sm text-gray-500">per hour</p>
              </div>
            </div>

            <p className="text-gray-600 leading-relaxed whitespace-pre-line">
              {project.description}
            </p>
          </div>

          {/* Pending Verification Card - Show when status is pending */}
          {/* {userProject.status === "pending" && userProject.verificationLink && (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <Shield className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-amber-900 mb-1">
                    Complete Your Verification
                  </h3>
                  <p className="text-sm text-amber-700 mb-4">
                    Your application is pending verification. Please complete
                    the verification process to proceed with this project.
                  </p>

                  <div className="bg-white rounded-xl border border-amber-200 p-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                      <LinkIcon className="w-4 h-4" />
                      <span>Verification Link</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-700 truncate font-mono">
                          {userProject.verificationLink}
                        </p>
                      </div>
                      <button
                        onClick={handleCopyLink}
                        className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex-shrink-0"
                        title="Copy link"
                      >
                        {copiedLink ? (
                          <Check className="w-5 h-5 text-green-600" />
                        ) : (
                          <Copy className="w-5 h-5 text-gray-600" />
                        )}
                      </button>
                    </div>
                    {copiedLink && (
                      <p className="text-xs text-green-600 mt-2">
                        Link copied to clipboard!
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 mt-4">
                    <button
                      onClick={handleGoToVerification}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity shadow-lg shadow-amber-500/25"
                    >
                      <ExternalLink className="w-5 h-5" />
                      Open Verification Link
                    </button>
                  </div>

                  <div className="mt-4 flex items-start gap-2 p-3 bg-amber-100/50 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700">
                      If the button doesn't work, copy the link above and paste
                      it in your browser. Make sure to complete all verification
                      steps.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )} */}

          {/* Rejection Reason */}
          {userProject.status === "rejected" && userProject.rejectionReason && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-red-800 mb-1">
                    Application Rejected
                  </h3>
                  <p className="text-red-700">{userProject.rejectionReason}</p>
                </div>
              </div>
            </div>
          )}

          {/* Termination Reason */}
          {userProject.status === "terminated" &&
            userProject.terminationReason && (
              <div className="bg-gray-100 border border-gray-200 rounded-2xl p-6">
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">
                      Project Terminated
                    </h3>
                    <p className="text-gray-700">
                      {userProject.terminationReason}
                    </p>
                  </div>
                </div>
              </div>
            )}

          {/* Responsibilities */}
          {project.responsibilities && project.responsibilities.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-gray-400" />
                Responsibilities
              </h2>
              <ul className="space-y-3">
                {[...project.responsibilities]
                  .sort((a, b) => a.order - b.order)
                  .map((resp, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#8B52FF]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-medium text-[#8B52FF]">
                          {resp.order}
                        </span>
                      </div>
                      <span className="text-gray-600">{resp.description}</span>
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status & Action Card */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sticky top-6">
            {/* Current Status */}
            <div
              className={`flex items-center gap-2 p-3 rounded-xl ${statusConfig.bg} border ${statusConfig.border} mb-6`}
            >
              <StatusIcon
                className={`w-5 h-5 ${statusConfig.text} ${statusConfig.animate ? "animate-spin" : ""}`}
              />
              <span className={`font-medium ${statusConfig.text}`}>
                {statusConfig.label}
              </span>
            </div>

            <h3 className="font-semibold text-gray-900 mb-4">
              {userProject.status === "assigned"
                ? "Ready to apply?"
                : userProject.status === "approved"
                  ? "Ready to start?"
                  : userProject.status === "active"
                    ? "Continue working"
                    : userProject.status === "pending"
                      ? "Verification Required"
                      : "Project Status"}
            </h3>

            {/* Skills Required */}
            {project.skillsRequired && project.skillsRequired.length > 0 && (
              <div className="mb-6">
                <p className="text-sm text-gray-500 mb-2">Skills Required</p>
                <div className="flex flex-wrap gap-2">
                  {project.skillsRequired.map((skill) => (
                    <span
                      key={skill.name}
                      className={`px-2.5 py-1 text-xs font-medium rounded-lg border border-gray-200 ${
                        skill.required
                          ? "bg-gray-100 text-gray-700"
                          : "bg-gray-50 text-gray-500"
                      }`}
                    >
                      {skill.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Slots Info */}
            {project.totalSlots && (
              <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-500">Available Slots</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {project.availableSlots} / {project.totalSlots}
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] rounded-full"
                    style={{
                      width: `${((project.totalSlots - (project.availableSlots || 0)) / project.totalSlots) * 100}%`,
                    }}
                  />
                </div>
              </div>
            )}

            {/* Action Button */}
            {actionButton && (
              <button
                onClick={actionButton.onClick}
                disabled={actionButton.disabled}
                className={`w-full py-3.5 font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 ${
                  actionButton.disabled
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : "bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30 hover:-translate-y-0.5 active:translate-y-0"
                }`}
              >
                {actionButton.loading && (
                  <Loader2 className="w-5 h-5 animate-spin" />
                )}
                {!actionButton.loading && actionButton.icon && (
                  <actionButton.icon className="w-5 h-5" />
                )}
                {actionButton.text}
              </button>
            )}

            {/* Pending Status Display with Quick Actions */}
            {userProject.status === "pending" && (
              <>
                <div className="w-full py-3.5 bg-amber-50 border border-amber-200 text-amber-700 font-medium rounded-xl text-center mb-3">
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Application under review
                  </div>
                </div>

                {userProject.verificationLink && (
                  <button
                    onClick={handleGoToVerification}
                    className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Complete Verification
                  </button>
                )}
              </>
            )}

            {/* Completed Status Display */}
            {userProject.status === "completed" && (
              <div className="w-full py-3.5 bg-emerald-50 border border-emerald-200 text-emerald-700 font-medium rounded-xl text-center">
                <div className="flex items-center justify-center gap-2">
                  <FileCheck className="w-5 h-5" />
                  Project Completed
                </div>
              </div>
            )}

            {/* Rejected/Terminated Status Display */}
            {(userProject.status === "rejected" ||
              userProject.status === "terminated") && (
              <div className="w-full py-3.5 bg-gray-100 border border-gray-200 text-gray-600 font-medium rounded-xl text-center">
                <div className="flex items-center justify-center gap-2">
                  <XCircle className="w-5 h-5" />
                  {userProject.status === "rejected"
                    ? "Application Rejected"
                    : "Project Terminated"}
                </div>
              </div>
            )}

            {/* Info Note for Assigned */}
            {userProject.status === "assigned" && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-xl flex items-start gap-3">
                <Shield className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">
                  You'll need to complete a verification step after applying.
                  This helps ensure quality work.
                </p>
              </div>
            )}

            {/* Status Info Messages */}
            {userProject.status === "pending" && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-xl">
                <p className="text-xs text-amber-700">
                  {userProject.verificationLink
                    ? "Please complete the verification process using the link above to proceed."
                    : "Your application is being reviewed. We'll notify you once it's approved."}
                </p>
              </div>
            )}

            {userProject.status === "approved" && (
              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                <p className="text-xs text-emerald-700">
                  Your application has been approved! Click above to start
                  working on this project.
                </p>
              </div>
            )}

            {userProject.status === "active" && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-xl">
                <p className="text-xs text-blue-700">
                  You're actively working on this project. Click above to access
                  the project platform.
                </p>
              </div>
            )}

            {userProject.status === "completed" && (
              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                <p className="text-xs text-emerald-700">
                  Great job! You've successfully completed this project. Thank
                  you for your contribution!
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Application Modal */}
      {showApplicationModal && (
        <ProjectApplicationModal
          projectTitle={project.title}
          verificationLink={userProject.verificationLink}
          onClose={() => setShowApplicationModal(false)}
          onSubmit={handleApply}
          isPending={applyMutation.isPending}
        />
      )}
    </div>
  );
};

export default ProjectDetail;
